import React, { useCallback, useMemo, useState } from "react";
import TableContainer from "../../components/Common/TableContainer";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { Button, Card, CardBody, Container } from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import useFetchData from "../../custom/Utils/UseFetchDataUtils";
import usePagination from "../../custom/Utils/UsePaginationUtils";
import useListFilters from "../../custom/Utils/UseListFiltersUtils";
import { formatDate } from "../../services/api.service";
import { listPageData } from "./CourierFile";
import { smartSort } from "../../custom/Utils/ColumnUtils";
import SmartImportButton from "../../custom/components/Import/SmartImportButton.js";

const breadcrumbItems = [];

const CourierList = () => {
  const navigate = useNavigate();
  const [exportData] = useState([]);

  const {
    payload,
    endpoint,
    searchfields,
    exportFileName,
    navigatePage,
    filterConfig,
  } = listPageData();
  const initialPayload = useMemo(() => payload, []);

  const {
    page,
    pageSize,
    nextPage,
    previousPage,
    lastPage,
    firstPage,
    handlePageSizeChange,
  } = usePagination();

  // list filters (config comes from CourierFile.js)
  const {
    filters,
    filterValues,
    handleFilterChange,
    resetFilters,
    filterWhereCondition,
    appliedFilterCount,
  } = useListFilters(filterConfig);

  const { data, isLoading, setSearchKeyword, totalCount, refetch } = useFetchData(
    initialPayload,
    page,
    pageSize,
    endpoint,
    searchfields,
    { filterWhere: filterWhereCondition, onResetPage: firstPage },
  );

  const handleViewClick = (id) => navigate(`${navigatePage.view}/${id}`);
  const handleCreate = () => navigate(navigatePage.create);

  const handlePageSizeChangeWrapper = useCallback(
    (newSize) => {
      handlePageSizeChange(newSize, totalCount);
    },
    [totalCount],
  );

  const columns = useMemo(
    () => [
      {
        Header: "SR.NO",
        accessor: (row) => page * pageSize + row.index,
        disableFilters: true,
        filterable: false,
        Cell: ({ value }) => <div style={{ width: "50px" }}>{value}</div>,
      },
      {
        Header: "OUTGOING INVOICE NO",
        accessor: (row) => row?.invoice_number || "-",
        disableFilters: true,
        filterable: false,
        sortType: smartSort,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },
      {
        Header: "COURIER NAME",
        accessor: (row) => row?.courier_name || "-",
        disableFilters: true,
        filterable: false,
        sortType: smartSort,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },
      {
        Header: "COURIER DOCKET NO",
        accessor: (row) => row?.courier_docket_number || "-",
        disableFilters: true,
        filterable: false,
        sortType: smartSort,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },
      {
        Header: "COURIER DOCKET DATE",
        accessor: (row) =>
          row?.courier_docket_date ? formatDate(row?.courier_docket_date) : "-",
        disableFilters: true,
        filterable: false,
        sortType: smartSort,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },
      {
        Header: "ZONE",
        accessor: (row) => row?.zone?.zone_name || "-",
        disableFilters: true,
        filterable: false,
        sortType: smartSort,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },
      {
        Header: "BRANCH",
        accessor: (row) => row?.branch?.branch_name || "-",
        disableFilters: true,
        filterable: false,
        sortType: smartSort,
        Cell: ({ value }) => <div style={{ width: "150px" }}>{value}</div>,
      },
      {
        Header: "ACTION",
        accessor: "actions",
        disableFilters: true,
        filterable: false,
        // every row here already has status = COURIER_COMPLETED (the list's
        // own whereCondition), so courier details are always already
        // submitted - no edit action, view only.
        Cell: ({ row }) => (
          <div style={{ width: "100px", display: "flex" }}>
            <div onClick={() => handleViewClick(row.original.id)}>
              <Link className="me-3 text-primary">
                <i className="mdi mdi-eye font-size-18"></i>
              </Link>
            </div>
          </div>
        ),
      },
    ],
    [page, pageSize, data],
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: "10px",
            }}
          >
            <Breadcrumb title="Courier List" breadcrumbItems={breadcrumbItems} />
            <Button
              color="primary"
              className="waves-effect waves-light me-1"
              onClick={handleCreate}
              style={{ marginLeft: "auto", background: "#006eb9" }}
            >
              Add Courier Details
            </Button>
            <SmartImportButton
              module="outgoing-invoice"
              label="Courier"
              onImported={refetch}
            />
          </div>

          <Card>
            <CardBody>
              <TableContainer
                columns={columns || []}
                data={data || []}
                isLoading={isLoading}
                onSearchInputData={setSearchKeyword}
                pageIndexProp={page}
                onPageSizeChange={handlePageSizeChangeWrapper}
                nextToPage={nextPage}
                lastToPage={() => lastPage(totalCount)}
                previousToPage={previousPage}
                previousFirstToPage={firstPage}
                totalCount={totalCount}
                customPageSize={pageSize}
                filters={filters}
                filterValues={filterValues}
                onFilterChange={handleFilterChange}
                onFilterReset={resetFilters}
                appliedFilterCount={appliedFilterCount}
                exportData={exportData}
                exportFileName={exportFileName}
              />
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default CourierList;
